import { Lock } from "lucide-react";
import { DocShell, DocCard } from "../components/DocShell";

export default function Privacy() {
  return (
    <DocShell
      pageTestId="privacy-page"
      icon={Lock}
      eyebrow="Your Privacy Matters"
      title="Privacy Policy"
      subtitle="How we collect, use and protect your information at Haplap."
      updated="June 2026">
      <DocCard testId="privacy-intro">
        <p>
          Haplap ("Haplap", "we", "us") is a community platform based in Newfoundland, Canada. We
          believe asking for or offering help should never cost you your privacy. This policy
          explains what we collect, why, and the choices you have. By using Haplap you agree to this
          policy.
        </p>
      </DocCard>

      <DocCard heading="Information we collect" testId="privacy-collect">
        <p>We only collect what we need to run the community safely:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Account details</strong> — your name, email, password (stored encrypted), and, if you sign in with Google, your basic Google profile.</li>
          <li><strong>Profile content</strong> — anything you choose to add: your story, needs, goals, tags, photos and posts.</li>
          <li><strong>Giving & wallet activity</strong> — tips, jar contributions, pledges, gift cards and cash-outs, so we can keep an accurate ledger.</li>
          <li><strong>Payment information</strong> — processed by our payment provider (Stripe). We do not store your full card numbers.</li>
          <li><strong>Usage & device data</strong> — basic technical information needed to keep the service secure and working.</li>
        </ul>
      </DocCard>

      <DocCard heading="How we use your information" testId="privacy-use">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To operate your account, profile, wallet and giving.</li>
          <li>To process payments and keep transaction records.</li>
          <li>To moderate the community and enforce our zero-tolerance safety rules.</li>
          <li>To send you notifications, thank-yous and important service messages.</li>
          <li>To improve and grow the platform.</li>
        </ul>
        <p>We do <strong>not</strong> sell your personal information to anyone.</p>
      </DocCard>

      <DocCard heading="Public vs private profiles" testId="privacy-visibility">
        <p>
          <strong>Members</strong> have public profiles so the community can find and support them.
          <strong> Supporters</strong> are private by default — hidden from Discovery and search —
          and can give anonymously. You control your visibility and privacy from your profile
          settings at any time. Fully anonymous gifts are hidden even from the recipient.
        </p>
      </DocCard>

      <DocCard heading="Sharing with service providers" testId="privacy-sharing">
        <p>
          We share limited data only with trusted providers who help us run Haplap — for example our
          payment processor (Stripe) for payments and cash-outs, and our hosting and email providers.
          They may only use your data to provide their service to us. We may also disclose
          information if required by law or to protect the safety of our community.
        </p>
      </DocCard>

      <DocCard heading="Your rights & choices" testId="privacy-rights">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Access, update or correct your information from your profile settings.</li>
          <li>Request a copy of your data or ask us to delete your account.</li>
          <li>Control notifications, private messages and profile visibility.</li>
          <li>Give publicly, name-hidden, or fully anonymously.</li>
        </ul>
        <p>To exercise any of these, contact us at <a className="text-copper underline" href="mailto:privacy@haplap.ca">privacy@haplap.ca</a>.</p>
      </DocCard>

      <DocCard heading="Data security & retention" testId="privacy-security">
        <p>
          We use industry-standard measures to protect your data, including encrypted passwords and
          secure payment handling. No system is perfectly secure, but we work hard to keep your
          information safe. We keep transaction records as long as needed for legal, accounting and
          fraud-prevention purposes, even after content is hidden or an account is closed.
        </p>
      </DocCard>

      <DocCard heading="Children" testId="privacy-children">
        <p>
          Haplap is intended for adults. It is not directed to children under the age of majority in
          their region, and we do not knowingly collect their information.
        </p>
      </DocCard>

      <DocCard heading="Changes & contact" testId="privacy-contact">
        <p>
          We may update this policy from time to time; the "last updated" date above will always
          reflect the current version. Questions about your privacy? Email{" "}
          <a className="text-copper underline" href="mailto:privacy@haplap.ca">privacy@haplap.ca</a>.
        </p>
      </DocCard>
    </DocShell>
  );
}
